export default defineNuxtRouteMiddleware(async to => {
	// Canonicalize the pathname before CMS matching and layout checks. Keep query
	// values (including embedded URLs) and the fragment untouched.
	const normalizedPath = to.path.replace(/\/{2,}/g, '/');
	if (normalizedPath !== to.path) {
		return navigateTo({path: normalizedPath, query: to.query, hash: to.hash}, {redirectCode: 301, replace: true});
	}

	const nuxtApp = useNuxtApp();

	// add trailing slash to url if missing. Ignore urls ending with .html, media files, or starting with /api/
	const noSlashExtensions = /\.(html|jpg|jpeg|png|gif|webp|svg|ico|mp4|mp3|pdf|zip|css|js|woff2?|ttf|eot)$/i;
	if (!to.path.endsWith('/') && to.path !== '/' && !noSlashExtensions.test(to.path) && !to.path.startsWith('/api/')) {
		const {path, query, hash} = to;
		const nextPath = `${path}/`;
		const nextRoute = {path: nextPath, query, hash};
		return navigateTo(nextRoute, {redirectCode: 301});
	}

	// reload app if routes are not loaded
	const routes = nuxtApp.$appData.routes;
	if (!routes) {
		throw createError({
			statusCode: 401,
			statusMessage: `Error 401`,
		});
	}

	const config = useAppConfig();
	const page = usePage({route: to});
	const publish = usePublish({route: to});
	const catalog = useCatalog({route: to});
	const {matchRoute, getRoute, getAppUrl} = useApiRoutes({route: to});

	if (config.catalog?.redirectEmptySearch && to.path === getAppUrl('catalog') && to.query.search_q !== undefined) {
		const searchTerm = Array.isArray(to.query.search_q) ? to.query.search_q.at(-1) : to.query.search_q;
		if (!searchTerm?.trim()) {
			const {search_q, ...query} = to.query;
			return navigateTo({path: to.path, query, hash: to.hash}, {replace: true});
		}
	}

	let template = 'CmsDefault';
	let controller = 'cms';
	let action = 'index';
	let contentType = 'page';
	let languageUrls = [];
	const bodyClass = ref();
	const {getUrlSegments, getLanguageUrls, getLastUrlSegment} = useUrl();
	const removeLast = __FILTER_URL_TYPE__ == 3 && to.path.includes(':');
	const routeSegments = getUrlSegments(to.path, {ignoreLang: true, removeLast: removeLast});
	const auth = useAuth({route: to});
	const isStaff = auth.getUser()?.staff;
	let globalData = {};
	to.meta.languageUrls = [];
	to.meta.categoryLevel = null;
	to.meta.faqCategory = null;

	// clear .app_cache_data cache
	if (to.query?.devclearcache) {
		const cacheModules = to.query.devclearcache.split(',');
		if (cacheModules?.length) {
			await $fetch(`/api/nuxtapi/clearCache/`, {
				method: 'POST',
				body: cacheModules,
			});
		}
	}

	// import structured data composable if enabled
	const structuredDataSchema = ref(null);
	let structuredData = null;
	if (__STRUCTURED_DATA__) structuredData = useStructuredData({route: to});

	const matchingRoute = matchRoute(routes, to.path); // match current url with api routes to get correct module
	const currentRoute = getRoute(matchingRoute); // get current route object

	// set controller, action and content type for active module
	if (currentRoute?.controller) controller = currentRoute.controller;
	if (currentRoute?.action) action = currentRoute.action;
	if (currentRoute?.content) contentType = currentRoute.content;
	if (currentRoute?.content_type) contentType = currentRoute.content_type;
	const extraContentType = currentRoute?.extra_content || null;

	// Reset state data
	if (controller != 'cms') {
		const pageState = useState('baseCmsPage', () => null);
		pageState.value = null;
	}

	// convert template names to match file names
	function setTemplate(payload) {
		if (payload) {
			const t = payload.split(/(?:_|\/|-)/);

			// if last item in array is "index", remove it
			if (t[t.length - 1] == 'index') t.pop();

			const tpl = t.map(el => {
				return el.charAt(0).toUpperCase() + el.slice(1);
			});
			const newTpl = tpl.join('');

			template = newTpl.toLowerCase();
			return newTpl;
		}

		return 'CmsDefault';
	}

	function setBodyClass(payload) {
		if (payload) {
			const bodyClass = payload.split(/(?:_|\/|-)/);

			if (bodyClass.length > 1) {
				const bClass = bodyClass.map(el => el.toLowerCase());
				return bClass.join('-');
			}

			return payload.toLowerCase();
		}

		return '';
	}

	// Check access to content if content is not visible. Only staff users can see hidden content.
	function checkAccess(data = null) {
		let dataObj = {
			template: '',
		};

		// If __CHECK_ACCESS__ is disabled, deny access to all users (show 404)
		if (!__CHECK_ACCESS__) {
			dataObj.template = 'Error404';
			if (import.meta.server) {
				const event = useRequestEvent();
				setResponseStatus(event, 404);
			}
		}

		if (__CHECK_ACCESS__) {
			if (import.meta.server) {
				dataObj.template = 'ErrorLoading';

				// Set page response status to 404
				const event = useRequestEvent();
				setResponseStatus(event, 404);
			}
			if (import.meta.client && !isStaff) {
				dataObj.template = 'Error404';
			}
			if (config?.hooks?.checkAccess) {
				// Execute checkAccess hook if defined in app.config
				const ca = config.hooks.checkAccess({contentType, controller, action, isStaff, data});
				dataObj = {...dataObj, ...ca};
			}
		}

		return dataObj;
	}

	// cms pages
	if (controller == 'cms') {
		let p = await page.fetch({slug: to.path});

		// FAQ categories are not CMS routes. Only accept a category confirmed by HAPI.
		if (!getRoute(to.path) && !p && routeSegments.length > 1) {
			const rootLength = getUrlSegments(to.path).length - routeSegments.length + 1;
			const faqPagePath = getUrlSegments(to.path, {limit: rootLength, stringify: true, addSlashes: true});

			if (getRoute(faqPagePath)) {
				const faqPage = await page.fetch({slug: faqPagePath});
				const faqTemplate = faqPage?.template ? faqPage.template.split('/').shift() : null;
				if (faqTemplate == 'faq') {
					const slug = getUrlSegments(to.path, {ignoreLang: true, offset: 1, stringify: true});
					const categories = await useFaq().fetchCategories({slug, mode: 'full'});
					if (categories?.success !== true) {
						throw createError({statusCode: 503, statusMessage: 'FAQ temporarily unavailable'});
					}
					const category = categories.data?.find(item => item?.id);
					if (category) {
						p = faqPage;
						to.meta.faqCategory = {id: category.id, pagePath: faqPagePath};
					}
				}
			}
		}

		bodyClass.value = `page-${controller}`;

		// match regular cms page
		if (p) {
			template = setTemplate(p.template);
			if (p.template) {
				bodyClass.value += ` page-` + setBodyClass(p.template);
			}

			languageUrls = getLanguageUrls(p);
			to.meta.pageId = p.id;
			if (structuredData) structuredDataSchema.value = structuredData.generateStructuredData({data: p, controller, template});

			// Allow only staff users to see unpublished pages
			if (!p.visible) {
				const ca = checkAccess();
				if (ca.template) template = ca.template;
			}
		} else {
			template = 'Error404';
		}
	}

	// match auth
	function matchAuth() {
		template = routeSegments[1] ? setTemplate('Auth-' + routeSegments[1]) : setTemplate('AuthDefault');
		bodyClass.value = routeSegments[1] ? 'page-auth page-auth-' + routeSegments[1] : `page-auth page-auth-${action}`;

		// if user is trying to confirm signup, set specific template and content type
		if (routeSegments[1] == 'confirm_signup') {
			template = 'AuthSignup';
			contentType = 'confirmSignup';
			return;
		}

		// if user is trying to confirm special signup, set specific template and content type
		if (routeSegments[1] == 'signup_confirm') {
			template = 'AuthSignup';
			contentType = 'confirmSignup';
			return;
		}

		// if forgotten password, set NewPassword template
		if (routeSegments[1] == 'change_password' && routeSegments.length == 4) {
			template = 'AuthNewPassword';
			contentType = 'newPassword';
			return;
		}

		if (!config.keycloak && !getRoute(to.path)) {
			template = 'Error404';
		}
	}

	if (contentType == 'auth') {
		matchAuth();
	}

	// match landing page
	if (controller == 'staticcontent') {
		bodyClass.value = `page-${controller}`;
		template = setTemplate(controller + '-Default');

		// if there is no match in routes for current landing page, show 404 template
		if (!getRoute(to.path)) {
			template = 'Error404';
		}
	}

	// match search results
	if (to.query.search_content || contentType == 'search') {
		template = 'Search';
	}

	// newsletter and sweepstake
	if (['newsletter', 'sweepstake'].includes(controller)) {
		bodyClass.value = `page-${controller}`;
		template = setTemplate(controller + '-' + action);
	}

	// webshop
	if (controller == 'webshop') {
		bodyClass.value = `page-${controller} page-${controller}-${action}`;
		template = getRoute(currentRoute.path) ? 'Webshop' + setTemplate(action) : 'Error404';
		if (action == 'view_order') {
			to.meta.layout = 'blank';
		}
	}

	// publish
	if (controller == 'publish') {
		bodyClass.value = `page-${controller}-${action}`;

		// if category page, fetch current category
		if (action == 'index' && contentType != 'publish_authors') {
			const currentCategory = await publish.fetchCategoryBySlug({slug: to.path}).then(res => (res?.data?.length ? res.data[0] : null));
			template = 'Publish';

			// generate structured data
			if (currentCategory && structuredData) {
				structuredDataSchema.value = structuredData.generateStructuredData({data: currentCategory, contentType, controller, action});
			}

			// if category has custom template, use it and set body class
			if (currentCategory?.template) {
				template = setTemplate('Publish-' + currentCategory.template);

				// If subfolder template does not exist, use default template
				if (!nuxtApp.$appData?.templates?.has(template)) template = 'Publish';

				bodyClass.value += ` page-${controller}-${action}-${currentCategory.template}`;
			}

			// set category id
			if (currentCategory?.id) globalData.entityId = currentCategory.id;

			// set language urls
			languageUrls = getLanguageUrls(currentCategory);

			// if there is no match in routes for current category, show 404 template
			if ((!getRoute(to.path) || !currentCategory) && contentType != 'tag') {
				template = 'Error404';
			}
		}

		// if detail page, fetch post and set post template
		if (action == 'detail' && contentType != 'publish_author') {
			const post = await publish.fetchPost({item_slug: to.path});
			template = 'PublishDetail';

			// if redirect url is set, redirect
			if (post?.redirect_url_without_domain) {
				return navigateTo({path: post.redirect_url_without_domain, query: {...to.query}}, {redirectCode: post.redirect_code ? post.redirect_code : 301});
			}

			// if post has category template, use it and set body class
			if (post?.category_template) {
				template = setTemplate('Publish-' + post.category_template + '-Detail');

				// If category template does not exist, use root template
				if (!nuxtApp.$appData?.templates?.has(template)) template = 'PublishDetail';

				bodyClass.value += ` page-${controller}-${action}-${post.category_template}`;
			}

			// set global data if post data exists
			if (post?.id) {
				globalData.entityId = post.id;
				globalData.feedbackType = controller;
				if (structuredData) structuredDataSchema.value = structuredData.generateStructuredData({data: post, contentType, controller, action});
			}

			// set language urls
			languageUrls = getLanguageUrls(post);

			// Allow only staff users to see unpublished posts
			const isPostVisible = Number(post?.status) >= 3 && Number(post?.category_visible);
			if (!isPostVisible) {
				const ca = checkAccess(post);
				if (ca.template) template = ca.template;
			}

			// if there is no post data or unauthorized, show 404 template
			if (!post?.id || ['error_unauthorized', 'error_empty_data'].includes(post?.label_name)) {
				template = 'Error404';
			}
		}

		// if tag page
		if (__TAGS__ && action == 'index_tag') {
			template = 'Tag';
			bodyClass.value += ` page-${controller}-${action}`;
		}

		// Publish authors
		if (__PUBLISH_AUTHORS__ && ['publish_authors', 'publish_author'].includes(contentType)) {
			template = contentType == 'publish_authors' ? 'PublishAuthors' : 'PublishAuthorDetail';
			bodyClass.value = `page-publish-${action}`;
		}
	}

	// catalog
	if (controller == 'catalog') {
		bodyClass.value = `page-${controller}-${action}`;
		// if route query contains search_q, set content type to search
		if (to.query?.search_q) contentType = 'search';

		if (contentType) bodyClass.value += ` page-${controller}-${contentType}`;
		const isMainLanding = routeSegments.length <= 1 && __CATALOG_MAIN_LANDING__ && !to.query?.search_q && catalog.isCatalogRoot(to.fullPath) ? true : false;

		// set category template
		if (action == 'index' && !isMainLanding) {
			// fetch current category and set category template if exists. If not, use default template
			let catalogCategory;
			if (contentType == 'category') {
				const categoryFetchOptions = config?.catalog?.fetchCategories ? config.catalog.fetchCategories : {};
				catalogCategory = await catalog.fetchCategoryBySlug({slug: to.path, ...categoryFetchOptions}).then(res => {
					if (res?.data?.redirect_url_without_domain) return res.data;
					return res?.data?.length ? res.data[0] : null;
				});
			}

			if (contentType == 'list') {
				const listFetchOptions = config?.catalog?.fetchLists ? config.catalog.fetchLists : {};
				catalogCategory = await catalog.fetchListBySlug({slug: to.path, ...listFetchOptions}).then(res => {
					if (res?.data?.redirect_url_without_domain) return res.data;
					return res?.data?.length ? res.data[0] : null;
				});
			}

			if (contentType == 'manufacturer') {
				const manufacturerFetchOptions = config?.catalog?.fetchManufacturers ? config.catalog.fetchManufacturers : {};
				// for brand+category URLs (/brand-base/manufacturers/BRAND/cat1/cat2/) the brand slug is always
				// at routeSegments[2] (ignoreLang); getLastUrlSegment would return the last category slug instead
				const isManufacturerCategoryUrl = __MANUFACTURER_CATEGORY_URLS__ && extraContentType == 'category' && routeSegments.length > 3;
				const manufacturerSlug = isManufacturerCategoryUrl ? routeSegments[2] : getLastUrlSegment(to.path);
				catalogCategory = await catalog.fetchManufacturers({slug: manufacturerSlug, limit: 1, mode: 'full', ...manufacturerFetchOptions}).then(res => {
					if (res?.data?.redirect_url_without_domain) return res.data;
					return res?.data?.length ? res.data[0] : null;
				});

				// fetch the category (extra_kind) the brand page is scoped to — categories are not brand-specific,
				// so the same slug is fetched via the regular category endpoint, mirroring its standalone category URL
				if (isManufacturerCategoryUrl && catalogCategory) {
					const categorySegments = routeSegments.slice(3);
					const extraCategorySlug = __CATALOG_SEO_URL__ || __CATEGORY_WITHOUT_BASE_URL__ ? `/${categorySegments.join('/')}/` : `/${routeSegments[0]}/${categorySegments.join('/')}/`;
					const extraKind = await catalog.fetchCategoryBySlug({slug: extraCategorySlug}).then(res => (res?.data?.length ? res.data[0] : null));
					if (extraKind) catalogCategory.extra_kind = extraKind;
				}

				catalog.category.value = catalogCategory;
			}

			if (catalogCategory?.redirect_url_without_domain) {
				return navigateTo({path: catalogCategory.redirect_url_without_domain, query: {...to.query}}, {redirectCode: catalogCategory.redirect_code || 301});
			}

			const categoryTemplate = catalogCategory?.template ? catalogCategory.template : '';
			if (catalogCategory?.id) {
				globalData.entityId = catalogCategory.id;

				// Add category level to meta data (used for meta hooks)
				to.meta.categoryLevel = catalogCategory?.level ? Number(catalogCategory.level) : null;

				if (structuredData) structuredDataSchema.value = structuredData.generateStructuredData({data: catalogCategory, contentType, controller, action});
			}

			// Set category template. If category template does not exist, use root template
			template = setTemplate('Catalog-' + categoryTemplate + '-' + action);
			if (!nuxtApp.$appData?.templates?.has(template)) template = setTemplate('Catalog-' + action);

			// Use landing template if category is landing page and feature is enabled
			if (__CATALOG_CATEGORY_LANDING__ && catalogCategory?.landing_page) {
				template = 'CatalogCategoryLanding';
			}

			// If there is no HAPI list data, show 404 template
			if (contentType == 'list' && Array.isArray(catalogCategory) && catalogCategory.length == 0) {
				template = 'Error404';
			}

			// Allow only staff users to see hidden categories and lists
			if (catalogCategory?.hasOwnProperty('visible') && (catalogCategory?.visible == null || catalogCategory?.visible == '0')) {
				const ca = checkAccess(catalogCategory);
				if (ca.template) template = ca.template;
			}

			// set language urls
			languageUrls = getLanguageUrls(catalogCategory);

			// if there is no category data, and this is not main landing page, show 404 template
			if (routeSegments.length > 1 && !catalogCategory?.id && contentType == 'category') template = 'Error404';

			// check if this is root category page
			globalData.rootPage = action == 'index' && routeSegments.length <= 1 && !to.query?.search_q ? true : false;
		}

		// set language urls for manufacturer and main landing pages
		if (action == 'index_manufacturer' || isMainLanding) {
			const p = await page.fetch({slug: to.path});
			if (p?.languages_urls) {
				languageUrls = getLanguageUrls(p);
			}
		}

		// load root landing template only if this component exists and there is no search query
		if (isMainLanding) {
			template = 'CatalogMain';
			bodyClass.value += ' page-catalog-landing';
		}

		// set manufacturer template
		if (action == 'index_manufacturer') {
			template = 'CatalogIndexManufacturer';
		}

		// set compare template
		if (to.query?.special_view == 'compare') {
			template = 'CatalogCompare';
			bodyClass.value = 'page-catalog-index page-compare';
		}

		// set quick order template
		if (config.catalog?.quickOrder && to.query?.special_view == 'order_form') {
			template = 'CatalogQuickOrder';
			bodyClass.value = 'page-catalog-index page-quickorder';
		}

		if (__WEBSHOP_CART_LINK__ && to.query?.special_view && to.query?.cartlink) {
			return navigateTo({
				path: getAppUrl('webshop_shopping_cart'),
				query: {cartlink: to.query.cartlink},
			});
		}

		// set wishlist template
		if (action == 'wishlist') {
			template = 'CatalogWishlist';
			bodyClass.value = 'page-catalog-index page-wishlist';
		}

		// if tag page
		if (__TAGS__ && action == 'index_tag') {
			template = 'Tag';
			bodyClass.value += ` page-${controller}-${action}`;
		}

		// set seller template
		if (__SELLERS__ && action == 'index_seller') {
			contentType = 'seller';

			if (routeSegments.length == 1) {
				template = 'CatalogIndexSeller';
			}
			if (routeSegments.length == 2) {
				template = 'CatalogDetailSeller';
			}
			if (routeSegments.length > 2) {
				template = 'Catalog';
			}
		}

		// set product detail template
		if (action == 'detail') {
			const productFetchOptions = config?.catalog?.fetch ? config.catalog.fetch : {};
			productFetchOptions.item_slug = to.path;
			productFetchOptions.search_q = to.query?.search_q ? to.query.search_q : null;

			const product = await catalog.fetchProduct(productFetchOptions);
			template = 'CatalogDetail';

			// if redirect url is set, redirect
			if (product?.redirect_url_without_domain) {
				return navigateTo({path: product.redirect_url_without_domain, query: {...to.query}}, {redirectCode: product.redirect_code ? product.redirect_code : 301});
			}

			// Redirect unavailable product to custom url. If not defined, 404 template is shown
			if (config.catalog?.redirectUnavailableProduct && product?.label_name == 'error_non_existing_product') {
				return navigateTo(config.catalog.redirectUnavailableProduct.url, {
					query: {...to.query},
					redirectCode: config.catalog.redirectUnavailableProduct.redirectCode || 301,
				});
			}

			// if product has category template, use it. If not, use default template
			if (product?.category_template) {
				template = setTemplate('Catalog-' + product.category_template + '-Detail');
				if (!nuxtApp.$appData?.templates?.has(template)) template = 'CatalogDetail';
				if (product.category_template) {
					bodyClass.value += ` page-${controller}-${action}-${product.category_template}`;
				}
			}

			// Set product data. If there is no product data or unauthorized, show 404 template.
			if (product?.id) {
				if (structuredData) structuredDataSchema.value = structuredData.generateStructuredData({data: product, contentType, controller, action});
				languageUrls = getLanguageUrls(product);
				globalData.entityId = product.id;
				globalData.feedbackType = 'catalogproduct';

				// Allow only staff users to see unpublished product
				const categoryVisible = product?.category_visible == null || +product.category_visible ? true : false;
				let brandVisible = product?.manufacturer_visible == null || +product.manufacturer_visible ? true : false;
				if (product?.ignore_brand_visibility) brandVisible = true;
				const isProductVisible = +product?.visible && categoryVisible && brandVisible;
				if (!isProductVisible) {
					const ca = checkAccess(product);
					if (ca.template) template = ca.template;
					if (ca.redirectTo) {
						return navigateTo({path: ca.redirectTo, query: {...to.query}}, {redirectCode: 301});
					}
				}
			} else {
				template = 'Error404';
			}
		}
	}

	// Events
	if (__EVENTS__ && controller == 'event') {
		bodyClass.value = `page-${controller}-${action}`;
		template = setTemplate(controller + '-' + action);

		if (routeSegments.length > 1) {
			bodyClass.value = `page-${controller}-detail`;
			template = 'EventDetail';
		}
	}

	if (__LOCATIONS__ && controller == 'location') {
		bodyClass.value = `page-${controller}-${action}`;
		template = setTemplate(controller + '-' + action);
		if (routeSegments.length > 1) {
			template = 'LocationDetail';
		}
	}

	if (config.hooks?.resolveRoute) {
		const context = await config.hooks.resolveRoute({
			to, route: currentRoute,
			context: {controller, action, contentType, template, bodyClass: bodyClass.value},
		});
		if (context) {
			controller = context.controller ?? controller;
			action = context.action ?? action;
			contentType = context.contentType ?? contentType;
			template = context.template ?? template;
			bodyClass.value = context.bodyClass ?? bodyClass.value;
		}
	}

	// set global data and push to nuxtApp object
	globalData.controller = controller;
	globalData.action = action;
	globalData.contentType = contentType;
	globalData.extraContentType = extraContentType;
	globalData.template = template;
	globalData.bodyClass = bodyClass.value;
	globalData.structuredData = structuredDataSchema.value;
	nuxtApp.$appGlobalData = globalData;
	to.meta.template = template;
	to.meta.bodyClass = bodyClass.value;
	to.meta.controller = controller;
	to.meta.action = action;
	to.meta.contentType = contentType;
	to.meta.extraContentType = extraContentType;
	to.meta.languageUrls = languageUrls;
	to.meta.entityId = globalData.entityId;

	// preload component on first load to prevent page flickering
	if (import.meta.client && nuxtApp.isHydrating && template) {
		await prefetchComponents(template);
	}

	// set page layout. If there is no layouts set in config for current template, use default layout
	if (config.layouts?.length) {
		const pageLayout = config.layouts.find(item => item.template.includes(template));
		await new Promise(resolve => setTimeout(resolve, 100));
		to.meta.layout = pageLayout ? pageLayout.layout : 'default';
	}

	if (import.meta.server && template === 'Error404') {
		const event = useRequestEvent();

		const blockedPatterns = [/^\/([a-z]{2}\/)?comments\/catalogproduct/, /^\/([a-z]{2}\/)?komentari\/catalogproduct/, /^\/([a-z]{2}\/)?komentari\/publish/];
		if (blockedPatterns.some(pattern => pattern.test(to.path)) || to.path.startsWith('/error/') || to.path.startsWith('/api/')) {
			// Set 410 (Gone) status for specific urls that should not be indexed by search engines and are not relevant for users. This is to prevent search engines from crawling and indexing these urls, which can negatively affect SEO.
			setResponseStatus(event, 410);
		} else {
			// Set 404 status for other urls that are not found
			setResponseStatus(event, 404);
		}
	}
});
